'use client'

// @ts-strict-ignore
import clsx from 'clsx'
import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectors } from 'store/selectors'

import { Card } from '@/entities/card'
import { TProductExtended } from '@/entities/card/types/types'

import { getProductClick } from '@/shared/integrations/yandex-metric'
import { pushToDataLayer } from '@/shared/integrations/yandex-metric/utils/pushToDataLayer'
import { CatalogItemLocation } from '@/shared/types/product'
import { OrderType, ProductControl, TProduct } from '@/shared/types/product-types'

import { BottomControllerOrder } from '../BottomControllerOrder/BottomControllerOrder'
import { BottomControllerPrereservation } from '../BottomControllerPrereservation/BottomControllerPrereservation'
import { BottomControllerReservation } from '../BottomControllerReservation/BottomControllerReservation'
import { TopController } from '../TopController/TopController'

import cl from './ProductCard.module.scss'

interface ProductCardProps {
	className?: string
	data: TProduct
	location?: CatalogItemLocation | `${CatalogItemLocation}`
	position?: number
	type?: 'regular' | 'wide' | 'wideOnMobile'
	adaptive?: boolean
	noFastOrder?: boolean
	blank?: boolean
	disableSlider?: boolean
	changeCompareIcon?: boolean
	isRatingMobileCompact?: boolean
}

export const ProductCard = memo((props: ProductCardProps) => {
	const {
		className,
		data,
		location,
		position,
		type = 'regular',
		adaptive,
		noFastOrder,
		disableSlider,
		changeCompareIcon,
		isRatingMobileCompact
	} = props
	const {
		KOD,
		SECTION_ID,
		ORDER_TYPE,
		TITLE,
		IMAGES,
		PRICE,
		OLD_PRICE,
		VISIBLE_PRICE,
		PROMOTION,
		IMAGES_RESIZE,
		IS_NEW,
		IMAGE,
		BUTTON_NAME,
		REVIEWS_SUMMARY,
		CONTROL
	} = data as TProductExtended
	const isPriceHidden = CONTROL?.includes(ProductControl.HidePrice)
	const isBuyHidden = CONTROL?.includes(ProductControl.HideBuy)
	const hideBadge = isPriceHidden && isBuyHidden

	const buttonName = BUTTON_NAME?.split('')
		.map((item, index) => (index === 0 ? item.toUpperCase() : item.toLowerCase()))
		.join('')
	const categories = useSelector(selectors.categoriesItems)

	const sendGtmData = useCallback(() => {
		pushToDataLayer(getProductClick(data, location, position + 1, categories))
	}, [categories, data, location, position])

	const cardData = useMemo(
		() => ({
			title: TITLE,
			price: PRICE?.VALUE,
			oldPrice: OLD_PRICE?.VALUE,
			images: IMAGES_RESIZE || IMAGES || [IMAGE],
			productCode: KOD,
			tags: PROMOTION,
			IS_NEW: Array.isArray(IS_NEW) ? IS_NEW[0] === 'Да' : IS_NEW === 'Да',
			reviewsSummary: REVIEWS_SUMMARY
		}),
		[IMAGES, KOD, OLD_PRICE?.VALUE, PRICE?.VALUE, PROMOTION, TITLE, IMAGES_RESIZE, IS_NEW, IMAGE, REVIEWS_SUMMARY]
	)

	const topController = (
		<TopController
			productCode={KOD}
			sectionId={SECTION_ID}
			itemClassName={cl.top_controller__item}
			changeCompareIcon={changeCompareIcon}
			data={cardData}
		/>
	)

	let isUnavailable = false
	let bottomController: JSX.Element

	if (ORDER_TYPE === OrderType.Order || ORDER_TYPE === OrderType.PreOrder || ORDER_TYPE === OrderType.TCP) {
		bottomController = (
			<BottomControllerOrder
				type={type}
				productData={data}
				noFastOrder={noFastOrder}
				price={PRICE.VALUE}
				buttonName={buttonName}
			/>
		)
	}

	if (ORDER_TYPE === OrderType.Reservation) {
		bottomController = (
			<BottomControllerReservation
				type={type}
				productData={data}
				price={PRICE.VALUE}
				buttonName={buttonName}
				hideBadge={hideBadge}
			/>
		)
	}

	if (ORDER_TYPE === OrderType.Prereservation) {
		bottomController = (
			<BottomControllerPrereservation type={type} productData={data} price={PRICE.VALUE} buttonName={buttonName} />
		)
	}

	if (ORDER_TYPE === OrderType.HideBuy || ORDER_TYPE === OrderType.Archive) {
		bottomController = <p className={cl.unavailable_text}>{buttonName ?? 'Нет в наличии'}</p>
		isUnavailable = true
	}

	return (
		<Card
			className={clsx(cl.root, className)}
			onClick={sendGtmData}
			type={type}
			data={cardData}
			topController={topController}
			bottomController={isBuyHidden ? null : bottomController}
			unavailable={isUnavailable}
			adaptive={adaptive}
			showPrice={VISIBLE_PRICE && !isPriceHidden}
			disableSlider={disableSlider}
			isRatingMobileCompact={isRatingMobileCompact}
		/>
	)
})
